import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useAuth } from "react-oidc-context";
import _ from "lodash";
import { Button, CircularProgress } from "@mui/material";
import { icons } from "../../../svg";
import { useNitroStore } from "../../../store/Store";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import "./mobileFavoriteOriginPanel.css";
import { useMutateFavoriteOrigins } from "../../../hooks/useMutateFavoriteOrigins";
import NitroService from "../../../utils/nitro.service";
import AddOriginColumn, {
  SelectedOrigin,
} from "../../../pages/home/favoriteOrigin/AddOriginColumn";
import {
  IFavoriteOrigin,
  IFavoriteOrigins,
  OriginDisplayInfo,
  PriceField,
} from "../../../types/IFavoriteOrigins";
import OriginRow from "../multiLocQuotePanel/components/originRow";
import GuidancePanel from "../../guidancePanel";
import IndicationHistoryPanel from "../../../pages/home/favoriteOrigin/IndicationHistoryPanel";
import InsightsPanel from "../../insightPanel";
import AddIndicationPanel from "../../../pages/home/favoriteOrigin/AddIndicationPanel";
import FooterMultiLocation from "../multiLocQuotePanel/components/footerMultiLocation";
import MobileSubFooter from "../multiLocQuotePanel/components/mobileSubFooter";

type OriginType = "koch" | "competitor";
type FilterType = "all" | "koch" | "competitor";

interface MobileFavoriteOriginsPanelProps {
  onSearchableChange?: (isSearchable: boolean) => void;
  onClose?: () => void;
  onCloseAll?: () => void;
  onShowTapHere?: (show: boolean) => void;
  onFooterSelect?: (tab: any) => void;
  selectedMonth?: string;
  setSelectedMonth?: (value: string) => void;
}

const MobileFavoriteOriginsPanel: React.FC<MobileFavoriteOriginsPanelProps> = ({
  onSearchableChange,
  onClose,
  onCloseAll,
  onShowTapHere,
  onFooterSelect,
  selectedMonth,
  setSelectedMonth,
}) => {
  const [
    globalProduct,
    priorityProducts,
    showGuidancePanel,
    setShowGuidancePanel,
    showIndicationHistoryPanel,
    setShowIndicationHistoryPanel,
    setSelectedFavoriteOrigin,
    refetchIndicationHistory,
    locations,
    setSelectedLocation,
    setTempPinLocation,
    openInsightsPanel,
    setShowAddIndicationHistoryPanel,
    showAddIndicationHistoryPanel,
  ] = useNitroStore((state) => [
    state.product,
    state.priorityProducts,
    state.showGuidancePanel,
    state.setShowGuidancePanel,
    state.showIndicationHistoryPanel,
    state.setShowIndicationHistoryPanel,
    state.setSelectedFavoriteOrigin,
    state.refetchIndicationHistory,
    state.locations,
    state.setSelectedLocation,
    state.setTempPinLocation,
    state.openInsightsPanel,
    state.setShowAddIndicationHistoryPanel,
    state.showAddIndicationHistoryPanel,
  ]);
  const triggerRefetchMultiLocation = useNitroCAStore(
    (state) => state.triggerRefetchMultiLocation,
  );

  const auth = useAuth();
  const user = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;

  const selectedProduct = globalProduct || "AMMONIA";

  const [filterType, setFilterType] = useState<FilterType>("all");

  const [isSwitchingProduct, setIsSwitchingProduct] = useState(false);

  const [isAddingKoch, setIsAddingKoch] = useState(false);
  const [isAddingCompetitor, setIsAddingCompetitor] = useState(false);
  const [currentAddingType, setCurrentAddingType] = useState<OriginType | null>(
    null,
  );
  const [isAddingOriginLoading, setIsAddingOriginLoading] = useState(false);
  const [removingOriginId, setRemovingOriginId] = useState<string | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [isUpdatingOrigin, setIsUpdatingOrigin] = useState(false);

  const [initialProduct, setInitialProduct] = useState(
    globalProduct || priorityProducts[0] || "AMMONIA",
  );
  const [initialKochCount, setInitialKochCount] = useState(0);
  const [initialCompetitorCount, setInitialCompetitorCount] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);

  const [pendingPriceChanges, setPendingPriceChanges] = useState<{
    [originId: string]: {
      [field in PriceField]?: string;
    };
  }>({});
  const debounceTimers = React.useRef<{ [originId: string]: NodeJS.Timeout }>(
    {},
  );
  const pendingRef = useRef<typeof pendingPriceChanges>(pendingPriceChanges);

  useEffect(() => {
    pendingRef.current = pendingPriceChanges;
  }, [pendingPriceChanges]);

  // keep-add/update spinner timers so they remain visible a short while after API completes
  const addHideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const updateHideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const SPINNER_HIDE_DELAY_MS = 600;

  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
      if (addHideTimerRef.current) clearTimeout(addHideTimerRef.current);
      if (updateHideTimerRef.current) clearTimeout(updateHideTimerRef.current);
    };
  }, []);

  const [allFavoriteOrigins, setAllFavoriteOrigins] = useState<
    IFavoriteOrigins[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const currentFetchRef = useRef<string | null>(null);
  const overlayRootRef = useRef<HTMLElement | null>(null);

  const fetchFavoriteOrigins = useCallback(
    async (forceRefresh = false, productOverride?: string) => {
      const productToUse = productOverride || selectedProduct;
      if (!user || !productToUse) {
        setAllFavoriteOrigins([]);
        setIsLoading(false);
        currentFetchRef.current = null;
        return;
      }
      const fetchKey = `${user}-${productToUse}`;

      if (!forceRefresh && currentFetchRef.current === fetchKey) {
        return;
      }

      setIsLoading(true);
      currentFetchRef.current = fetchKey;

      try {
        const result = await NitroService.getFavoriteOrigins(user);

        if (currentFetchRef.current === fetchKey) {
          setAllFavoriteOrigins(result);
        }
      } catch (error) {
        if (currentFetchRef.current === fetchKey) {
          setAllFavoriteOrigins([]);
        }
      } finally {
        if (currentFetchRef.current === fetchKey) {
          setIsLoading(false);
          currentFetchRef.current = null;
        }
      }
    },
    [user, selectedProduct],
  );

  useEffect(() => {
    fetchFavoriteOrigins();
  }, [fetchFavoriteOrigins]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    let root = document.getElementById("mobile-overlay-root");
    if (!root) {
      root = document.createElement("div");
      root.id = "mobile-overlay-root";
      root.style.position = "fixed";
      root.style.top = "0";
      root.style.left = "0";
      root.style.right = "0";
      root.style.bottom = "0";
      root.style.zIndex = "100000";
      root.style.pointerEvents = "none";
      document.body.appendChild(root);
    }
    overlayRootRef.current = root as HTMLElement;
  }, []);

  const sortByCreateDate = (a: any, b: any) => {
    const dateA = a.create_date;
    const dateB = b.create_date;
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    if (dateA.year !== dateB.year) return dateB.year - dateA.year;
    if (dateA.month !== dateB.month) return dateB.month - dateA.month;
    if (dateA.day !== dateB.day) return dateB.day - dateA.day;
    if (dateA.hour !== dateB.hour) return dateB.hour - dateA.hour;
    if (dateA.minute !== dateB.minute) return dateB.minute - dateA.minute;
    return dateB.second - dateA.second;
  };

  const { kochOrigins, competitorOrigins } = useMemo(() => {
    const originsForProduct =
      allFavoriteOrigins.find(
        (o) => o.product.toUpperCase() === selectedProduct.toUpperCase(),
      )?.favorites || [];

    const koch: IFavoriteOrigin[] = [];
    const competitor: IFavoriteOrigin[] = [];

    originsForProduct.forEach((origin) => {
      if (origin.competitor) {
        competitor.push(origin);
      } else {
        koch.push(origin);
      }
    });

    return {
      kochOrigins: koch.sort(sortByCreateDate),
      competitorOrigins: competitor.sort(sortByCreateDate),
    };
  }, [allFavoriteOrigins, selectedProduct]);

  const splitMonthKeys = useMemo(() => {
    const keys = new Set<string>();
    [...kochOrigins, ...competitorOrigins].forEach((origin) => {
      (origin.months || []).forEach((monthData) => {
        if (monthData.isSplit) {
          const monthKey =
            typeof monthData.month === "string"
              ? (monthData.month as string).slice(0, 7)
              : `${(monthData.month as any).year}-${String(
                  (monthData.month as any).month,
                ).padStart(2, "0")}`;
          keys.add(monthKey);
        }
      });
    });

    return Array.from(keys);
  }, [kochOrigins, competitorOrigins]);

  const { addOrigin, removeOrigin } = useMutateFavoriteOrigins({
    user,
    product: selectedProduct,
  });
  const [isSearchable, setIsSearchable] = useState(false);

  const [hasStartedLoading, setHasStartedLoading] = useState(false);

  useEffect(() => {
    if (isLoading && isSwitchingProduct) {
      setHasStartedLoading(true);
    }
  }, [isLoading, isSwitchingProduct]);

  useEffect(() => {
    if (!isLoading && isSwitchingProduct && hasStartedLoading) {
      setIsSwitchingProduct(false);
      setHasStartedLoading(false);
    }
  }, [isLoading, isSwitchingProduct, hasStartedLoading]);

  useEffect(() => {
    if (!isLoading && user && selectedProduct) {
      setInitialProduct(selectedProduct);
      setInitialKochCount(kochOrigins.length);
      setInitialCompetitorCount(competitorOrigins.length);
      setHasChanges(false);
    }
  }, [isLoading, user, selectedProduct, kochOrigins, competitorOrigins]);

  useEffect(() => {
    const productChanged = selectedProduct !== initialProduct;
    const kochCountChanged = kochOrigins.length !== initialKochCount;
    const competitorCountChanged =
      competitorOrigins.length !== initialCompetitorCount;

    setHasChanges(productChanged || kochCountChanged || competitorCountChanged);
  }, [
    selectedProduct,
    kochOrigins,
    competitorOrigins,
    initialProduct,
    initialKochCount,
    initialCompetitorCount,
  ]);

  useEffect(() => {
    const favoriteOriginsData: IFavoriteOrigins[] = [
      {
        product: selectedProduct,
        favorites: [...kochOrigins, ...competitorOrigins],
      },
    ];

    const searchableValue = favoriteOriginsData[0]?.favorites?.length === 0;

    setIsSearchable(searchableValue);

    if (onSearchableChange) {
      onSearchableChange(searchableValue);
    }

    if (kochOrigins.length || competitorOrigins.length) {
      setPendingPriceChanges((prev) => {
        const updated = { ...prev };
        let hasPriceChanges = false;

        Object.entries(prev).forEach(([originId, pendingFields]) => {
          const allOrigins = [...kochOrigins, ...competitorOrigins];
          const backendOrigin = allOrigins.find(
            (o) => o.location.id === originId,
          );
          if (!backendOrigin) return;

          const fieldsToCheck = Object.entries(pendingFields);
          const allFieldsMatch = fieldsToCheck.every(
            ([field, pendingValue]) => {
              const backendValue =
                backendOrigin[`${field}Price` as keyof typeof backendOrigin];
              return String(backendValue ?? "") === pendingValue;
            },
          );

          if (allFieldsMatch && fieldsToCheck.length > 0) {
            delete updated[originId];
            hasPriceChanges = true;
          }
        });

        return hasPriceChanges ? updated : prev;
      });
    }
  }, [kochOrigins, competitorOrigins, selectedProduct, onSearchableChange]);

  const handleAddOrigin = async (
    origin: SelectedOrigin | undefined,
    type: OriginType,
  ) => {
    if (!origin) {
      setCurrentAddingType(null);
      setIsAddingKoch(false);
      setIsAddingCompetitor(false);
      return;
    }

    if (addHideTimerRef.current) {
      clearTimeout(addHideTimerRef.current);
      addHideTimerRef.current = null;
    }
    setIsAddingOriginLoading(true);

    try {
      await addOrigin(origin, type);
      await fetchFavoriteOrigins(true);
      triggerRefetchMultiLocation();
    } catch (error) {
      console.error("Failed to add origin:", error);
    } finally {
      if (addHideTimerRef.current) clearTimeout(addHideTimerRef.current);
      addHideTimerRef.current = setTimeout(() => {
        setIsAddingOriginLoading(false);
        addHideTimerRef.current = null;
      }, SPINNER_HIDE_DELAY_MS);

      setCurrentAddingType(null);
      if (type === "koch") {
        setIsAddingKoch(false);
      } else {
        setIsAddingCompetitor(false);
      }
    }
  };

  const handleCloseAddOriginPanel = () => {
    setIsAddingOriginLoading(false);
    setCurrentAddingType(null);
    setIsAddingKoch(false);
    setIsAddingCompetitor(false);
  };

  const handleRemoveOrigin = async (id: string, type: OriginType) => {
    const locationId = id.replace(`${type}-`, "");
    setRemovingOriginId(id);

    try {
      await removeOrigin(locationId, type);
      await fetchFavoriteOrigins(true);
    } catch (error) {
      // Error removing origin
    } finally {
      setRemovingOriginId(null);
    }
  };

  const handleUpdateOrigin = (
    id: string,
    field: PriceField,
    value: string,
    type: OriginType,
  ) => {
    setPendingPriceChanges((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));

    if (debounceTimers.current[id]) {
      clearTimeout(debounceTimers.current[id]);
    }

    debounceTimers.current[id] = setTimeout(async () => {
      const allChangesForOrigin = pendingRef.current[id];

      if (!allChangesForOrigin) {
        delete debounceTimers.current[id];
        return;
      }

      const originsList = type === "koch" ? kochOrigins : competitorOrigins;
      const locationId = id.replace(`${type}-`, "");
      const originToUpdate = originsList.find(
        (o) => o.location.id === locationId,
      );

      if (!originToUpdate) {
        delete debounceTimers.current[id];
        return;
      }

      const updatedOrigin = { ...originToUpdate } as any;
      const changedFields = Object.keys(allChangesForOrigin) as PriceField[];

      changedFields.forEach((fieldKey) => {
        const fieldValue = allChangesForOrigin[fieldKey];
        if (fieldValue === undefined) return;

        if (
          fieldKey === "truckEnabled" ||
          fieldKey === "railEnabled" ||
          fieldKey === "bargeEnabled"
        ) {
          updatedOrigin[fieldKey] = fieldValue === "true";
          return;
        }

        const numValue = fieldValue === "" ? null : Number(fieldValue);
        updatedOrigin[`${fieldKey}Price`] = numValue;
      });

      try {
        if (updateHideTimerRef.current) {
          clearTimeout(updateHideTimerRef.current);
          updateHideTimerRef.current = null;
        }
        setIsUpdatingOrigin(true);
        await NitroService.updateFavoriteOrigin(
          updatedOrigin,
          user,
          selectedProduct,
        );

        await fetchFavoriteOrigins(true);
        triggerRefetchMultiLocation();

        setPendingPriceChanges((currentPending) => {
          const next = { ...currentPending };
          delete next[id];
          return next;
        });
      } catch (e) {
        console.error("Failed to update favorite origin:", e);
      } finally {
        if (updateHideTimerRef.current)
          clearTimeout(updateHideTimerRef.current);
        updateHideTimerRef.current = setTimeout(() => {
          setIsUpdatingOrigin(false);
          updateHideTimerRef.current = null;
        }, SPINNER_HIDE_DELAY_MS);

        delete debounceTimers.current[id];
      }
    }, 700);
  };

  const handleMenuAction = async (action: any, origin: OriginDisplayInfo) => {
    const locationId = origin.id.includes("-")
      ? origin.id.substring(origin.id.indexOf("-") + 1)
      : origin.id;
    const fullOrigin =
      action === "remove"
        ? null
        : [...kochOrigins, ...competitorOrigins].find(
          (o) => o.location.id === locationId,
        );
    if (fullOrigin) {
      setSelectedFavoriteOrigin([fullOrigin]);
    }

    switch (action) {
      case "overview": {
        const selectedFavoriteOrigin = _.find(locations, (location) => {
          return location.id === locationId;
        });

        if (
          selectedFavoriteOrigin &&
          selectedFavoriteOrigin.pinType === "zipCodes"
        ) {
          return;
        }

        if (!selectedFavoriteOrigin) {
          console.warn("Overview target not found in locations", {
            locationId,
          });
          try {
            const details = await NitroService.getLocationDetails(locationId);
            const lat = Number(details?.coordinates?.[0]);
            const lng = Number(details?.coordinates?.[1]);

            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              setTempPinLocation({ lat, lng });
            }
          } catch (err) {
            console.error("Failed to load overview location details", err);
          }

          openInsightsPanel(locationId, "Logistics", false);
          break;
        }
        setSelectedLocation(selectedFavoriteOrigin);
        setTempPinLocation({
          lat: selectedFavoriteOrigin.lat,
          lng: selectedFavoriteOrigin.long,
        });

        openInsightsPanel(locationId, "Logistics", false);
        break;
      }
      case "guidance":
        setShowGuidancePanel(true);
        break;
      case "add-fob":
        setShowAddIndicationHistoryPanel(true);
        break;
      case "history":
        if (fullOrigin) {
          refetchIndicationHistory(
            fullOrigin.location.id,
            fullOrigin.location.name,
          );
        }
        setShowIndicationHistoryPanel(true);
        break;
      case "remove": {
        const originType = origin.id.split("-")[0] as OriginType;
        handleRemoveOrigin(origin.id, originType);
        break;
      }
      default:
        break;
    }
  };

  const renderOriginSection = (
    type: OriginType,
    origins: any[],
    isAdding: boolean,
    setIsAdding: (adding: boolean) => void,
  ) => {
    const isKoch = type === "koch";
    const title = isKoch ? "Koch Origins" : "Competitor Origins";

    return (
      <section
        className="origins-section-mobile"
        id={`favorite-origins-section-id-${type}`}
      >

        {origins
          .filter((fav) => fav)
          .map((fav) => {
            const rowType: OriginType = fav.competitor ? "competitor" : "koch";
            const uniqueId = `${rowType}-${fav.location.id}`;
            const pending = pendingPriceChanges[uniqueId] || {};
            return (
              <OriginRow
                key={uniqueId}
                origin={{
                  id: uniqueId,
                  name: fav.location.name,
                  truckMin: pending.truckMin ?? String(fav.truckMinPrice ?? ""),
                  truckMax: pending.truckMax ?? String(fav.truckMaxPrice ?? ""),
                  railMin: pending.railMin ?? String(fav.railMinPrice ?? ""),
                  railMax: pending.railMax ?? String(fav.railMaxPrice ?? ""),
                  truckEnabled: String(fav.truckEnabled ?? false),
                  railEnabled: String(fav.railEnabled ?? false),
                  bargeEnabled: String(fav.bargeEnabled ?? false),
                }}
                onRemove={(id) => handleRemoveOrigin(id, rowType)}
                onUpdate={(id, field, value) =>
                  handleUpdateOrigin(id, field, value, rowType)
                }
                onAction={(actionType, originData) =>
                  handleMenuAction(actionType, originData)
                }
                activeMenuId={activeMenuId}
                setActiveMenuId={setActiveMenuId}
                type={rowType}
                onShowTapHere={onShowTapHere}
              />
            );
          })}

        {!isAdding && origins.length === 0 && (
          <div className="empty-state-container">
            <img
              data-tooltip-id="location-icon"
              src={icons.LocationIcon}
              alt="Location Icon"
              className="empty-state-icon"
            />
            <div className="empty-state-text">
              No {type} origins added yet
              {isKoch && <span style={{ color: "red" }}> (Required)</span>}
            </div>
            <div className="empty-state-subtext">
              Add origin and set FOB price
            </div>
          </div>
        )}

        {!isAdding && (
          <Button
            id={`add-origin-button-id-${type}`}
            className="add-origin-button"
            onClick={() => {
              setIsAdding(true);
              setCurrentAddingType(type);
              setIsAddingCompetitor(type === "competitor");
            }}
          >
            <span
              className="add-origin-text"
              style={{ color: title.includes("Koch") ? "#1D4ED8" : "#DC2626" }}
            >
              + Add {title.slice(0, -1)}
            </span>
          </Button>
        )}
      </section>
    );
  };

  const shouldHideSubFooter =
    showAddIndicationHistoryPanel || showIndicationHistoryPanel;

  return (
    <div
      id="mobile-indication-history-id-1"
      className="mobile-favorite-origins-panel-container"
    >
      {showGuidancePanel && (
        <GuidancePanel
          open={showGuidancePanel}
          close={() => setShowGuidancePanel(false)}
        />
      )}
      <InsightsPanel />

      <AddIndicationPanel />
      {showIndicationHistoryPanel && <IndicationHistoryPanel />}

      {currentAddingType && (
        <div className="add-origin-panel">
          <div
            className="add-origin-panel-content"
            style={{ display: isAddingOriginLoading ? "none" : "block" }}
          >
            <AddOriginColumn
              onAdd={(origin?: SelectedOrigin) =>
                handleAddOrigin(origin, currentAddingType)
              }
              onCancel={handleCloseAddOriginPanel}
              favoriteOrigins={
                currentAddingType === "koch"
                  ? (kochOrigins as IFavoriteOrigin[])
                  : (competitorOrigins as IFavoriteOrigin[])
              }
              context="mobileFavoriteOriginPanel"
              isCompetitor={isAddingCompetitor}
            />
          </div>
        </div>
      )}

      {isAddingOriginLoading &&
        (() => {
          const overlay = (
            <div
              className="adding-origin-overlay"
              style={{ pointerEvents: "auto" }}
            >
              <CircularProgress size="40px" className="adding-origin-spinner" />
              <div className="adding-origin-text">Adding origin...</div>
            </div>
          );
          return overlayRootRef.current
            ? createPortal(overlay, overlayRootRef.current)
            : overlay;
        })()}

      {isUpdatingOrigin &&
        (() => {
          const overlay = (
            <div
              className="adding-origin-overlay"
              style={{ pointerEvents: "auto" }}
            >
              <CircularProgress size="40px" className="adding-origin-spinner" />
              <div className="adding-origin-text">Updating origin...</div>
            </div>
          );
          return overlayRootRef.current
            ? createPortal(overlay, overlayRootRef.current)
            : overlay;
        })()}

      {removingOriginId &&
        (() => {
          const overlay = (
            <div
              className="adding-origin-overlay"
              style={{ pointerEvents: "auto" }}
            >
              <CircularProgress size="40px" className="adding-origin-spinner" />
              <div className="adding-origin-text">Removing origin...</div>
            </div>
          );
          return overlayRootRef.current
            ? createPortal(overlay, overlayRootRef.current)
            : overlay;
        })()}

      <div
        className="mobile-favorite-origins-content"
      >
        <div
          id="origin-manager-title-id"
          className="favorite-origin-header-row"
        >
          <Button
            id="favorite-origin-close-back"
            aria-label="Close"
            onClick={() => {
              triggerRefetchMultiLocation();

              if (onSearchableChange) {
                onSearchableChange(isSearchable);
              }

              if (onClose) {
                onClose();
              } else {
                useNitroStore
                  .getState()
                  .setShowMobileFavoriteOriginPanel(false);
              }

              if (hasChanges) {
                window.location.reload();
              }
            }}
          >
            <img
              data-tooltip-id="go-back-icon"
              src={icons.BackWithBackgroundIcon}
              alt="Go Back Icon"
              className="back-icon-favorite-origin"
            />
          </Button>
          <div
            id="favorite-origins-title-product"
            className="section-title-products"
          >
            Origin Manager
          </div>
        </div>


        <section className="products-section">
          <div
            id="origin-filter-toggle-container"
            className="origin-filter-toggle-container"
          >
            <Button
              className={`origin-filter-btn btn-all ${filterType === "all" ? "active" : ""
                }`}
              onClick={() => setFilterType("all")}
            >
              All
            </Button>
            <Button
              className={`origin-filter-btn btn-koch ${filterType === "koch" ? "active" : ""
                }`}
              onClick={() => setFilterType("koch")}
            >
              Koch
            </Button>
            <Button
              className={`origin-filter-btn btn-competitor ${filterType === "competitor" ? "active" : ""
                }`}
              onClick={() => setFilterType("competitor")}
            >
              Competitor
            </Button>
          </div>
        </section>

        {/* <header className="panel-header">
          <div className="header-title-group">
            <img
              data-tooltip-id="location-icon-blue"
              src={icons.LocationIconBlue}
              alt="Location Icon Blue"
            />
            <div className="favorite-origins-title">Favorite Origins</div>
          </div>
          <Button
            id="favorite-origin-close-button"
            aria-label="Close"
            onClick={() => {
              triggerRefetchMultiLocation();

              if (onSearchableChange) {
                onSearchableChange(isSearchable);
              }

              if (onClose) {
                onClose();
              } else {
                useNitroStore
                  .getState()
                  .setShowMobileFavoriteOriginPanel(false);
              }

              if (hasChanges) {
                window.location.reload();
              }
            }}
            sx={{
              minWidth: "auto",
              padding: "8px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <img
              data-tooltip-id="simple-close-icon"
              src={icons.SimpleCloseIcon}
              alt="Close Icon"
            />
          </Button>
        </header> */}


        {isLoading &&
          kochOrigins.length === 0 &&
          competitorOrigins.length === 0 &&
          (() => {
            const overlay = (
              <div
                className="adding-origin-overlay"
                style={{ pointerEvents: "auto" }}
              >
                <CircularProgress
                  size="40px"
                  className="adding-origin-spinner"
                />
                <div className="adding-origin-text">
                  Loading favorite origins...
                </div>
              </div>
            );
            return overlayRootRef.current
              ? createPortal(overlay, overlayRootRef.current)
              : overlay;
          })()}

        <div className="panel-scrollable-content">
          {isLoading &&
            kochOrigins.length === 0 &&
            competitorOrigins.length === 0 ? null : (
            <>
              {(filterType === "all" || filterType === "koch") &&
                renderOriginSection(
                  "koch",
                  kochOrigins,
                  isAddingKoch,
                  setIsAddingKoch,
                )}

              {(filterType === "all" || filterType === "competitor") &&
                renderOriginSection(
                  "competitor",
                  competitorOrigins,
                  isAddingCompetitor,
                  setIsAddingCompetitor,
                )}
            </>
          )}
        </div>

        <FooterMultiLocation
          selected="origins"
          onSelect={(item) => {
            if (item === "account") {
              const state = useNitroStore.getState();
              state.setShowSettingsPanel?.(true);
              state.setShowMobileFavoriteOriginPanel?.(false);
              state.setShowMobileSearchPanel?.(false);
            }

            if (item === "quote" || item === "account") {
              if (onClose) {
                onClose();
              }
            }
            if (onFooterSelect) {
              onFooterSelect(item);
            }
          }}
        />
        {!shouldHideSubFooter && (
          <MobileSubFooter
            onClose={onClose}
            onCloseAll={onCloseAll}
            onMapClick={() => {
              if (onFooterSelect) {
                onFooterSelect("origins");
              }
            }}
            forceMapButton
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            splitMonthKeys={splitMonthKeys}
          />
        )}
      </div>
    </div>
  );
};

export default MobileFavoriteOriginsPanel;
